<template>
    <div class="d-flex flex-column" style="gap: var(--space-5)">
        <div
            v-if="
                showScoresEnabled &&
                (audit.score_override >= 0 || audit.form_max_score) &&
                audit.completed_at &&
                isAuditComplete(audit)
            "
            class="w-100 d-flex justify-content-center align-items-center p-2 gap-3"
            style="
                background-color: var(--primary-25);
                border-radius: var(--radius-default);
            "
        >
            <h3 style="color: var(--neutral-800)">
                {{ $t('audits.panels.summary.overall') }}
            </h3>
            <ProgressCircle
                :percentage="
                    audit.score_override ??
                    (audit.form_max_score > 0
                        ? Math.round(
                              (audit.form_score / audit.form_max_score) * 100
                          )
                        : 0)
                "
            />
        </div>
        <div class="d-flex flex-column">
            <div v-if="audit.date" class="detail-list-item">
                <span class="label">{{
                    $t('audits.panels.summary.date')
                }}</span>
                <span class="value"
                    ><strong>{{ formatDisplayDate(audit.date) }}</strong></span
                >
            </div>
            <div v-if="formattedAuditType" class="detail-list-item">
                <span class="label">{{
                    $t('audits.panels.summary.type')
                }}</span>
                <span class="value"
                    ><strong>{{ formattedAuditType }}</strong></span
                >
            </div>
            <div
                v-if="audit.site_name && !$siteStore.isSingleSiteUserView"
                class="detail-list-item"
            >
                <span class="label">{{
                    $t('audits.panels.summary.site')
                }}</span>
                <span class="value"
                    ><strong>{{ audit.site_name }}</strong></span
                >
            </div>
            <div v-if="audit.auditor" class="detail-list-item">
                <span class="label">{{
                    $t('audits.panels.summary.auditor')
                }}</span>
                <span class="value"
                    ><strong>{{ audit.auditor }}</strong></span
                >
            </div>
            <div v-if="reviewerName" class="detail-list-item">
                <span class="label">{{
                    $t('audits.panels.summary.reviewer')
                }}</span>
                <span class="value"
                    ><strong>{{ reviewerName }}</strong></span
                >
            </div>
            <div v-if="audit.area_name" class="detail-list-item">
                <span class="label">{{
                    $t('audits.panels.summary.area')
                }}</span>
                <span class="value"
                    ><strong>{{ audit.area_name }}</strong></span
                >
            </div>
            <div v-if="audit.details" class="detail-list-item">
                <span class="label">{{
                    $t('audits.panels.summary.notes')
                }}</span>
                <strong class="value">{{ audit.details }}</strong>
            </div>
            <div
                v-if="audit.files && audit.files.length > 0"
                class="image-card"
            >
                <AdvancedUploadField
                    accept="image"
                    :multiple="false"
                    :max-image-height="500"
                    :max-image-width="500"
                    v-model="files"
                    :disabled="true"
                    :hideUploadField="true"
                />
            </div>
        </div>
        <div class="d-flex justify-content-end" style="gap: var(--space-3)">
            <button
                v-if="isAuditDraft(audit)"
                class="btn btn-primary"
                @click="this.$emit('continue-audit')"
            >
                Continue Audit
                <i class="fi fi-audit"></i>
            </button>
            <button
                v-else-if="isAuditComplete(audit) && audit.form_json"
                class="btn btn-primary"
                @click="this.$emit('view-audit')"
            >
                {{ $t('audits.panels.actions.viewAudit') }}
                <i class="fi fi-eye"></i>
            </button>
            <button
                v-else-if="isAuditScheduled(audit) && audit.template_id"
                class="btn btn-primary"
                @click="this.$emit('perform-audit')"
            >
                {{ $t('audits.panels.actions.performAudit') }}
                <i class="fi fi-add"></i>
            </button>
            <div
                v-if="canDownloadAuditPdf"
                class="generate-report-pdf-tooltip-wrap"
            >
                <ToolTip :max-width="18">
                    <template #tooltip-trigger>
                        <button
                            type="button"
                            class="btn btn-light"
                            :disabled="
                                pdfExportLoading || audit.published_to_client
                            "
                            @click="downloadAuditPdf"
                        >
                            <span v-if="pdfExportLoading">{{
                                $t('audits.panels.summary.preparing')
                            }}</span>
                            <template v-else>
                                {{ $t('audits.panels.actions.generateReport') }}
                                <i class="fi fi-audit"></i>
                            </template>
                        </button>
                    </template>
                    <template #tooltip-content>
                        {{ generateReportButtonTooltip }}
                    </template>
                </ToolTip>
            </div>
            <IconButton
                v-if="
                    $userStore.hasPerm('audits:write') &&
                    (!audit.app_admin_only || isAdminRoute)
                "
                preset="edit"
                @click="handleEditAudit"
            />
            <CopyButton
                v-if="isAdminRoute"
                :copy-text="copyText"
                :disabled="!copyText"
                :alert-text="$t('audits.panels.summary.linkCopied')"
                button-class="icon-btn icon-btn-light"
                :tooltip="$t('audits.panels.summary.copyLink')"
            />
            <IconButton
                v-if="
                    $userStore.hasPerm('audits:write') &&
                    (!audit.app_admin_only || isAdminRoute)
                "
                preset="delete"
                @click="handleDeleteAudit"
            />
        </div>
    </div>
</template>

<script>
import AdvancedUploadField from '../fields/AdvancedUploadField.vue';
import ProgressCircle from '../ProgressCircle.vue';
import CopyButton from '@/components/fields/CopyButton.vue';
import ToolTip from '@/components/fields/ToolTip.vue';
import IconButton from '@/components/IconButton.vue';

import { formatDisplayDate } from '@/utils/dateUtils';
import {
    canDisplayAuditScores,
    isObservationAndEvidenceEligibleAudit,
} from '@/utils/auditVisibility';
import {
    isAuditComplete,
    isAuditDraft,
    isAuditScheduled,
} from '@/utils/auditStatus';
import { useAuditStore } from '@/stores/audits';

export default {
    name: 'AuditSummaryView',
    components: {
        ProgressCircle,
        AdvancedUploadField,
        CopyButton,
        ToolTip,
        IconButton,
    },
    props: {
        audit: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            files: [],
            pdfExportLoading: false,
        };
    },
    emits: ['open-modal'],
    computed: {
        isAdminRoute() {
            return this.$route.matched.some((r) => r.name === 'Admin');
        },
        isCompanyDetailRoute() {
            return this.$route.name === 'Company Detail';
        },
        shouldCopyOverviewLink() {
            return isObservationAndEvidenceEligibleAudit(this.audit);
        },
        copyText() {
            if (!this.audit?.hash) return '';

            if (this.shouldCopyOverviewLink) {
                if (this.isCompanyDetailRoute && this.$route?.params?.hash) {
                    const queryParams = new URLSearchParams();
                    queryParams.set('tab', 'AUDITS');

                    const companyId =
                        this.audit?.company_id ??
                        this.$route?.query?.company_id;
                    if (companyId) {
                        queryParams.set('company_id', String(companyId));
                    }

                    return `${window.location.origin}/admin/company/${
                        this.$route.params.hash
                    }/audits/${
                        this.audit.hash
                    }/overview?${queryParams.toString()}`;
                }

                return `${window.location.origin}/audits/${this.audit.hash}/overview`;
            }

            return `${window.location.origin}/audits/${this.audit.hash}`;
        },
        formattedAuditType() {
            if (!this.audit?.audit_type) return null;
            const type = this.audit.audit_type;
            return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
        },
        canDownloadAuditPdf() {
            const isAppAdmin = this.$userStore.isAppAdmin;
            const isAppConsultant = Boolean(
                this.$userStore.user?.is_app_consultant
            );
            return (
                this.isAdminRoute &&
                Boolean(this.audit?.id) &&
                Boolean(this.audit?.template_id) &&
                isAuditComplete(this.audit) &&
                (isAppAdmin || isAppConsultant)
            );
        },
        generateReportButtonTooltip() {
            if (this.pdfExportLoading) {
                return this.$t('audits.panels.summary.reportTooltipPreparing');
            }
            if (this.audit?.published_to_client) {
                return this.$t('audits.panels.summary.reportTooltipPublished');
            }
            return this.$t('audits.panels.summary.reportTooltipReady');
        },
        showScoresEnabled() {
            return canDisplayAuditScores(this.audit);
        },
        reviewerName() {
            if (this.audit?.reviewer_name) return this.audit.reviewer_name;
            const reviewer = this.audit?.reviewer;
            if (typeof reviewer === 'string' && reviewer.trim()) {
                return reviewer.trim();
            }
            if (reviewer && typeof reviewer === 'object') {
                const fullName = `${reviewer.first_name || ''} ${
                    reviewer.last_name || ''
                }`.trim();
                return fullName || null;
            }
            return null;
        },
    },
    methods: {
        isAuditDraft,
        isAuditComplete,
        isAuditScheduled,
        formatDisplayDate,
        downloadAuditPdf() {
            if (!this.audit?.id) return;
            if (this.audit?.published_to_client) return;
            this.pdfExportLoading = true;
            this.$downloadFile('audit/export/pdf', {
                audit_id: this.audit.id,
                attach_to_audit: true,
            })
                .then(() => {
                    this.auditStore?.incrementAuditHistoryVersionKey();
                })
                .catch((error) => {
                    const errorMessage =
                        error?.message ||
                        this.$t('audits.panels.summary.downloadReportError');
                    this.$alertStore.pushError(errorMessage);
                })
                .finally(() => {
                    this.pdfExportLoading = false;
                    this.auditStore?.closePanel();
                });
        },
        handleEditAudit() {
            if (!this.$userStore.companyHasPerm('audits:write')) {
                this.$userStore.togglePremiumModal(true);
                return;
            }
            this.$emit('edit-audit');
        },
        handleDeleteAudit() {
            if (!this.$userStore.companyHasPerm('audits:write')) {
                this.$userStore.togglePremiumModal(true);
                return;
            }
            this.$emit('open-modal');
        },
    },
    created() {
        this.auditStore = useAuditStore();
    },
    mounted() {
        this.files = this.audit?.files ?? [];
    },
};
</script>

<style scoped>
.detail-list-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.625rem 0;
    border-bottom: 1px solid var(--neutral-600, #e6e6e6);
}

.image-card {
    margin-top: var(--space-3);
    padding: var(--space-3);
    gap: 0.75rem;
    background-color: var(--neutral-400);
    border-radius: var(--space-3);
}

.footer {
    width: 100%;
    height: 50px;
}

/* So hover still reaches the ToolTip trigger when the inner button is disabled. */
.generate-report-pdf-tooltip-wrap :deep(.tooltip-trigger .btn:disabled) {
    pointer-events: none;
}
</style>
